import { Autocomplete, Stack, TextField, Typography } from "@mui/material";
import { sortBy } from "lodash-es";
import { type FC, type SyntheticEvent, useCallback, useEffect, useMemo, useState } from "react";
import type { UUID } from "../../../../../../../../utils/uuid";
import type { MomentRecipientsClassify } from "../../../../../../hooks/use-moment-recipients-classify";
import { GreyBox } from "../../components/grey-box";
import { usePreviewContext } from "../../preview-context";
import {
  PreviewWithSidebarTargetingSegment,
  type SelectedDynamicTargeting,
} from "./preview-with-sidebar-targeting-segment";

export const PreviewWithSidebarAutocomplete: FC<{
  info: Exclude<MomentRecipientsClassify, { type: "unknown" | "channel" }>;
}> = ({ info: { type } }) => {
  const [selectedDynamicSegment, setSelectedDynamicSegment] = useState<SelectedDynamicTargeting>({
    type: "MOMENT_SEGMENT",
  });

  const {
    preview_info: { loading, data },
  } = usePreviewContext();

  useEffect(() => {
    if (loading || !data) return;

    if (selectedDynamicSegment.type === "MOMENT_SEGMENT") return;

    const selectedId = data.selectedPreviewInfo?.subject.id;

    if (!selectedId) return;

    if (!selectedDynamicSegment.data.info.data.some((x) => x.id === selectedId)) {
      data.setSelectedPreviewInfo(null);
    } else {
      const selectedSubject = data.subjects.find((x) => x.id === selectedId);

      if (selectedSubject) {
        data.setSelectedPreviewInfo(selectedSubject.id);
      }
    }
  }, [loading, data, selectedDynamicSegment]);

  const baseOptions = useMemo(() => {
    if (loading || !data?.subjects || data.subjects.length === 0) return [];

    if (type === "connection") {
      return sortBy(
        data.subjects.map((subject) => ({
          value: subject.id,
          label: `${subject.first_name} ${subject.last_name} (${subject.connection?.first_name} ${subject.connection?.last_name})`,
          email: subject.email,
        })),
        "label",
      );
    }

    return sortBy(
      data.subjects.map((subject) => ({
        value: subject.id,
        label: `${subject.first_name} ${subject.last_name}`,
        email: subject.email,
      })),
      "label",
    );
  }, [loading, data, type]);

  const options = useMemo(() => {
    if (selectedDynamicSegment.type === "MOMENT_SEGMENT") return baseOptions;

    return baseOptions.filter((option) =>
      selectedDynamicSegment.data.info.data.some((x) => x.id === option.value),
    );
  }, [baseOptions, selectedDynamicSegment]);

  const onChange = useCallback(
    (_: SyntheticEvent | null, value: { value: string; label: string } | null) => {
      if (loading || !data) return null;

      return data.setSelectedPreviewInfo((value?.value as UUID) ?? null);
    },
    [loading, data],
  );

  const { label, helperText } = useMemo(() => {
    if (type === "recipients") return { label: "Preview as recipient", helperText: undefined };

    if (type === "connection")
      return { label: "Preview subject", helperText: "Recipient appears in braces" };

    return { label: "Preview with subject", helperText: undefined };
  }, [type]);

  return (
    <>
      <PreviewWithSidebarTargetingSegment
        selected={selectedDynamicSegment}
        onChange={setSelectedDynamicSegment}
      />
      {!loading && options.length === 0 ? (
        <NoOptions />
      ) : (
        <Autocomplete
          loading={loading}
          options={options}
          getOptionLabel={(option) => option.label}
          onChange={onChange}
          value={options.find((x) => x.value === data?.selectedPreviewInfo?.subject.id) ?? null}
          renderInput={(params) => (
            <TextField {...params} label={label} variant="outlined" helperText={helperText} />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.value}>
              <Stack direction="column" spacing={0}>
                <Typography variant="body2">{option.label}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {option.email}
                </Typography>
              </Stack>
            </li>
          )}
        />
      )}
    </>
  );
};

const NoOptions: FC = () => {
  return (
    <GreyBox>
      <Typography variant="body2" color="textSecondary">
        Previewing with Test data - nobody is currently in the recipient segment
      </Typography>
    </GreyBox>
  );
};
